import { useInstance } from '@meraki-internal/react-dependency-injection';
import { IQueryParamDrivenDrawerContentProvider } from '../../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { CareMapState, ICareMapOnboardingStep } from '../../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { QueryParamDrivenDrawerPage } from '../../components/CareMapDrawerPage';
import { IonButton } from '@ionic/react';
import { ActiveCaremapNoteCreator } from './ActiveCaremapNoteCreator';
import { useNoteCreator } from './useNoteCreator';

interface IAddNoteDrawerProps {
    autoOpenFileChooser?: boolean;
    careMapOnboardingStepComplete?: ICareMapOnboardingStep;
    trackingFrom?: string;
    // start the note shared with viewers, whatever the care recipient's default
    shared?: boolean;
};

export class AddNoteWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IAddNoteDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IAddNoteDrawerProps>){}

    mode = 'add-note';

    Component = AddNoteDrawerView;

    open = (props?: IAddNoteDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    // The drawer over the notes page of a given care map, for links built away from that page.
    getUrlForCareMap = (careMapId: string, props?: IAddNoteDrawerProps) => {
        return `/notes/${careMapId}?${this.drawerHelper.buildQueryParams({ mode: this.mode, ...props })}`;
    };
};


const AddNoteDrawerView: React.FC<IAddNoteDrawerProps> = ({ autoOpenFileChooser, careMapOnboardingStepComplete, shared }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    const { noteVM, saveAndClose } = useNoteCreator({
        note: shared ? { isPrivate: false } : undefined,
        trackingFrom: drawerHelper.getDrawerParams().trackingFrom,
        onSaved: () => {
            drawerHelper.close();
            if (careMapOnboardingStepComplete) {
                careMapState.completeOnboardingStep(careMapOnboardingStepComplete);
            }
        }
    });

    return (
        <QueryParamDrivenDrawerPage
            title="Add Note"
            hasBack={false}
            data-type="note-editor"
            buttons={(
                <>
                    <IonButton
                        data-type="save-button"
                        disabled={!noteVM.canSave()}
                        onClick={saveAndClose}>Save</IonButton>
                    <IonButton
                        fill="outline"
                        data-type="cancel-button"
                        onClick={() => {
                            drawerHelper.close();
                        }}
                    >Cancel</IonButton>
                </>
            )}
        >
            <ActiveCaremapNoteCreator
                noteVM={noteVM}
                autoOpenFileChooser={autoOpenFileChooser}
                includeTemplates
            />
        </QueryParamDrivenDrawerPage>
    );
};
